import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from '../../components/Layout'
import { usePermissionStore } from '../../stores/permissionStore'
import AdminHome from './AdminHome'
import AdminUsers from './AdminUsers'
import AdminSessions from './AdminSessions'
import AdminApplications from './AdminApplications'
import AdminContent from './AdminContent'
import AdminReports from './AdminReports'
import AdminSecurity from './AdminSecurity'
import AdminEmailTemplates from './AdminEmailTemplates'
import AdminTermsPrivacy from './AdminTermsPrivacy'
import AdminSettings from './AdminSettings'

// Ordered list of modules → route paths for redirect fallback
const MODULE_ROUTES = [
  { module: 'dashboard',    path: 'home' },
  { module: 'users',        path: 'users' },
  { module: 'sessions',     path: 'sessions' },
  { module: 'applications', path: 'applications' },
  { module: 'content',           path: 'content' },
  { module: 'reports',           path: 'reports' },
  { module: 'security',          path: 'security' },
  { module: 'email_templates',   path: 'email-templates' },
  { module: 'terms_privacy',     path: 'terms-privacy' },
  { module: 'settings',          path: 'settings' },
]

/** Redirects to Dashboard if accessible, otherwise to the first accessible page. */
function AdminDefaultRedirect() {
  const { can, loaded, is_super_admin } = usePermissionStore()
  if (!loaded) return null  // wait for permissions before deciding

  if (is_super_admin || can('dashboard', 'view')) {
    return <Navigate to="home" replace />
  }

  const first = MODULE_ROUTES.find(({ module }) => can(module, 'view'))
  if (first) return <Navigate to={first.path} replace />

  // Authenticated admin with zero permissions
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>No Access</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
        Your account has no permissions assigned yet.<br />
        Contact your Super Admin to request access.
      </p>
    </div>
  )
}

/** Renders children only if the user has `can_view` for the given module.
 *  While permissions are still loading, renders nothing to prevent flash. */
function GuardedRoute({ module, children }: { module: string; children: React.ReactNode }) {
  const { can, loaded, is_super_admin } = usePermissionStore()
  if (!loaded) return null
  if (is_super_admin || can(module, 'view')) return <>{children}</>
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Access Denied</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
        You don't have permission to view this section.<br />
        Contact your Super Admin to request access.
      </p>
    </div>
  )
}

export default function AdminDashboard() {
  return (
    <Layout role="admin">
      <Routes>
        <Route index element={<AdminDefaultRedirect />} />
        <Route path="home" element={<GuardedRoute module="dashboard"><AdminHome /></GuardedRoute>} />
        <Route path="users" element={<GuardedRoute module="users"><AdminUsers /></GuardedRoute>} />
        <Route path="sessions" element={<GuardedRoute module="sessions"><AdminSessions /></GuardedRoute>} />
        <Route path="applications" element={<GuardedRoute module="applications"><AdminApplications /></GuardedRoute>} />
        <Route path="content" element={<GuardedRoute module="content"><AdminContent /></GuardedRoute>} />
        <Route path="reports" element={<GuardedRoute module="reports"><AdminReports /></GuardedRoute>} />
        <Route path="security" element={<GuardedRoute module="security"><AdminSecurity /></GuardedRoute>} />
        <Route path="email-templates" element={<GuardedRoute module="email_templates"><AdminEmailTemplates /></GuardedRoute>} />
        <Route path="terms-privacy" element={<GuardedRoute module="terms_privacy"><AdminTermsPrivacy /></GuardedRoute>} />
        <Route path="settings" element={<GuardedRoute module="settings"><AdminSettings /></GuardedRoute>} />
      </Routes>
    </Layout>
  )
}
